/**
 * Ranked list table with sortable headers and inline row expansion.
 *
 * Sort state lives in the closure so it survives header clicks, and each row's
 * scorecard is built lazily on first expand — with 101 possible rows, building every
 * scorecard up front would be wasteful.
 */
import type { Asset } from '../../core/types.ts';
import { esc, statusClass } from '../../core/format.ts';
import { buildHeadlineBar, buildTabs, type EnrichHandler } from './scorecard.ts';

type SortColumn = 'score' | 'name' | 'corridor' | 'sleeve';
type SortDirection = 'asc' | 'desc';

export function buildListTable(assets: readonly Asset[], onEnrich?: EnrichHandler): HTMLElement {
  const wrap = document.createElement('div');
  let sortBy: SortColumn = 'score';
  let sortDir: SortDirection = 'desc';

  function sortValue(a: Asset): string | number {
    switch (sortBy) {
      case 'score':
        return a.score;
      case 'name':
        return (a.name || '').toLowerCase();
      case 'corridor':
        return (a.corridor || '').toLowerCase();
      case 'sleeve':
        return (a.sleeve || '').toLowerCase();
    }
  }

  function compareAssets(a: Asset, b: Asset): number {
    const av = sortValue(a);
    const bv = sortValue(b);
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }

  const arrowFor = (col: SortColumn): string =>
    sortBy !== col
      ? '<span class="sort-arrow inactive"></span>'
      : `<span class="sort-arrow ${sortDir}"></span>`;
  const activeCls = (col: SortColumn): string => (sortBy === col ? ' active' : '');

  function build(): void {
    wrap.innerHTML = '';
    const sorted = [...assets].sort(compareAssets);

    const table = document.createElement('table');
    table.className = 'list-table';
    table.innerHTML =
      '<thead><tr>' +
      '<th>Ticker</th>' +
      `<th class="sortable${activeCls('name')}" data-sort="name">Issuer${arrowFor('name')}</th>` +
      `<th class="sortable corridor${activeCls('corridor')}" data-sort="corridor">Corridor${arrowFor('corridor')}</th>` +
      `<th class="sortable${activeCls('sleeve')}" data-sort="sleeve">Sleeve${arrowFor('sleeve')}</th>` +
      `<th class="sortable num${activeCls('score')}" data-sort="score">Atlas Score${arrowFor('score')}</th>` +
      '<th></th>' +
      '</tr></thead><tbody></tbody>';

    const tbody = table.querySelector('tbody')!;
    for (const a of sorted) {
      const row = document.createElement('tr');
      row.className = 'row-main';
      row.innerHTML =
        `<td class="ticker">${esc(a.tickers[0])}</td>` +
        `<td class="name">${esc(a.name)}</td>` +
        `<td class="corridor">${esc(a.corridor)} · ${esc(a.exchange)}</td>` +
        `<td class="sleeve">${esc(a.sleeve)}</td>` +
        `<td class="score ${statusClass(a.status)}">${a.score}</td>` +
        '<td class="expand-icon">+</td>';

      const detail = document.createElement('tr');
      detail.className = 'detail-row';
      detail.style.display = 'none';
      const td = document.createElement('td');
      td.colSpan = 6;
      const dwrap = document.createElement('div');
      dwrap.className = 'detail-wrap';
      td.appendChild(dwrap);
      detail.appendChild(td);

      let built = false;
      row.addEventListener('click', () => {
        const isOpen = row.classList.toggle('expanded');
        const icon = row.querySelector('.expand-icon');
        if (icon) icon.textContent = isOpen ? '−' : '+';
        if (isOpen && !built) {
          dwrap.appendChild(buildHeadlineBar(a));
          dwrap.appendChild(buildTabs(a, onEnrich));
          built = true;
        }
        detail.style.display = isOpen ? 'table-row' : 'none';
      });

      tbody.appendChild(row);
      tbody.appendChild(detail);
    }

    for (const th of table.querySelectorAll<HTMLTableCellElement>('th.sortable')) {
      th.addEventListener('click', () => {
        const col = th.dataset['sort'] as SortColumn | undefined;
        if (!col) return;
        if (sortBy === col) {
          sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
          sortBy = col;
          // First click: score high-to-low, text columns A-to-Z.
          sortDir = col === 'score' ? 'desc' : 'asc';
        }
        build();
      });
    }
    wrap.appendChild(table);
  }

  build();
  return wrap;
}
